import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { ZONE2_NAV_ITEMS } from '@/features/layout/logic';

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

interface SearchModalProps {
  onClose: () => void;
}

export function SearchModal({ onClose }: SearchModalProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filteredItems = query
    ? ZONE2_NAV_ITEMS.filter((item) =>
        t(item.labelKey).toLowerCase().includes(query.toLowerCase())
      )
    : ZONE2_NAV_ITEMS;

  const handleSelect = (href: string) => {
    navigate(href);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center pt-[20vh]">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-lg mx-4 bg-card rounded-lg border border-border shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={t('layout.search.title')}
      >
        <div className="flex items-center gap-2 px-4 border-b border-border">
          <SearchIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('layout.search.placeholder')}
            className="flex-1 h-12 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
          />
          <kbd className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">ESC</kbd>
        </div>
        <div className="max-h-72 overflow-y-auto p-2">
          {filteredItems.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{t('layout.search.noResults')}</p>
          ) : (
            filteredItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleSelect(item.href)}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left"
              >
                <span className="text-muted-foreground">{t(item.labelKey)}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
